import { Injectable } from '@angular/core';
import { HttpService } from 'projects/customer/src/app/shared/services/http-service.service';
import { HttpClient } from '@angular/common/http';
import {
    Service,
    Resource,
    DocumentCollection,
    DocumentResource
} from 'ngx-jsonapi';
import { Observable } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { CountriesModel } from '../../../shared/models';
import { AxiosAuthenticationService } from '../../../shared/services/axios-authentication.service';

export class Product extends Resource {
    public attributes = {
        type: '',
        id: ''
    };
    public relationships = {
        category: new DocumentResource<category>(),
        storage_type: new DocumentResource<storageType>(),
        product_stock: new DocumentResource<productStock>()
    };
}

@Injectable({ providedIn: 'root' })
export class storageType extends Resource {
    public attributes = {
        type: '',
        id: ''
    };
}
@Injectable({
    providedIn: 'root'
})
export class storageTypeservice extends AxiosAuthenticationService<storageType> {
    public resource = storageType;
    public type = 'storage_type';
}

@Injectable({ providedIn: 'root' })
export class category extends Resource {
    public attributes = {
        type: '',
        id: ''
    };
}
@Injectable({
    providedIn: 'root'
})
export class categoryservice extends AxiosAuthenticationService<category> {
    public resource = category;
    public type = 'category';
}

@Injectable({ providedIn: 'root' })
export class productAllService extends AxiosAuthenticationService<Product> {
    public resource = Product;
    public type = 'product';
}

@Injectable({ providedIn: 'root' })
export class productsAllService extends AxiosAuthenticationService<Product> {
    public resource = Product;
    public type = 'products';
}

@Injectable({ providedIn: 'root' })
export class BulkActivityService extends AxiosAuthenticationService<Product> {
    public resource = Product;
    public type = 'bulk_activity';
}

@Injectable({ providedIn: 'root' })
export class BulkActivitiesService extends AxiosAuthenticationService<Product> {
    public resource = Product;
    public type = 'bulk_activities';
}

@Injectable({
    providedIn: 'root'
})
export class ProductsService extends AxiosAuthenticationService<Product> {
    public resource = Product;
    public type = 'product';
    //   public parseFromServer(attributes): void {
    //     attributes.name = '' + attributes.name;
    // }
}

@Injectable({
    providedIn: 'root'
})
export class ProductService extends Service<Product> {
    private readonly listSearchTermsSubject = new BehaviorSubject<string[]>([]);
    private activeGridTabKey: string | null = null;

    /** Emits when the product list search terms change. */
    readonly listSearchTerms$ = this.listSearchTermsSubject.asObservable();

    constructor(private http: HttpClient, public httpService: HttpService) {
        super();
    }

    /** Replaces the shared product list search terms (trimmed, de-duplicated). */
    setListSearchTerms(terms: readonly string[]): void {
        const normalized = [...new Set(
            terms.map((term) => term.trim()).filter(Boolean)
        )];
        this.listSearchTermsSubject.next(normalized);
    }

    /** Returns the current product list search terms. */
    getListSearchTerms(): string[] {
        return [...this.listSearchTermsSubject.getValue()];
    }

    /** Clears all committed product list search terms. */
    clearListSearchTerms(): void {
        this.setListSearchTerms([]);
    }

    /**
     * Tracks grid tab navigation and clears search when the tab or module changes.
     * Does not run on view/edit child routes — call only from list grid tabs.
     */
    navigateProductGridTab(tabKey: string): void {
        if (this.activeGridTabKey !== null && this.activeGridTabKey !== tabKey) {
            this.clearListSearchTerms();
        }
        this.activeGridTabKey = tabKey;
    }

    /** Clears search terms and tab tracking (e.g. leaving the products grid area). */
    clearProductGridSearchState(): void {
        this.activeGridTabKey = null;
        this.clearListSearchTerms();
    }

    /**
     * Merges committed search terms into a grid filter object as `search_pattern`.
     * Each term maps to `filter[search_pattern][index]`.
     */
    withListSearchPattern(qs: Record<string, unknown> = {}): Record<string, unknown> {
        const merged = { ...qs };
        const terms = this.getListSearchTerms();

        if (terms.length) {
            merged.search_pattern = terms.map((term) => encodeURIComponent(term));
        } else {
            delete merged.search_pattern;
        }

        return merged;
    }

    createProduct(data) {
        const path = 'products/create';
        return this.httpService.callAuthPostApi(path, data, 'jsonApi');
    }

    getLanguageData() {
        const path = 'products/language';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    deleteProductImages(productId, data) {
        const path = 'products/' + productId + '/delete_product_images';
        return this.httpService.callPutApi(path, data, 'jsonApi');
    }

    getBrandsData() {
        const path = 'products/brand';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getProductCategoryData(warehouseCode?: string) {
        let path = 'products/product_category';
        if (warehouseCode) {
            path += `?warehouse_code=${encodeURIComponent(warehouseCode)}`;
        }
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getBrandData() {
        const path = 'products/brand';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getPackingMaterialData(warehouseCode: string, page = 1, pageSize = 10) {
        const path =
            `products/packaging_materials?warehouse_code=${encodeURIComponent(warehouseCode)}` +
            `&page[number]=${page}&page[size]=${pageSize}`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getSuppliersData() {
        const path = 'products/supplier';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getCSKU(pageno, searchPattern?) {
        let path = `products/get_kit_skus?page[number]=${pageno}`;
        if (searchPattern) {
            path = `products/get_kit_skus?filter[search_pattern][0]=${searchPattern}&page[number]=${pageno}`;
        }
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getStorageTypesData() {
        const path = 'products/storage_types';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getCurrencyData() {
        const path = 'products/currency';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getUomsData() {
        const path = 'products/uoms';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getProductTemplateFile() {
        const path = 'v1/masters/templates?type=3';
        return this.httpService.getFileAsBlob(path);
    }
    getProductTemplateFileKit() {
        const path = 'v1/masters/templates?type=4';
        return this.httpService.getFileAsBlob(path);
    }

    bulkUploadProductData(file) {
        const path = 'products/import';
        return this.httpService.callUploadFileServiceApi(path, file, 'jsonApi');
    }

    updateState(id, data) {
        const path = `products/${id}/update_state`;
        return this.httpService.callPutApi(path, data, 'jsonApi');
    }

    exportProduct(routetype, productId?, mp?, file_type?) {
        let path = `products/${routetype}/export`;
        if (productId) {
            path = path + `?product_ids=${productId}`;
        }
        if (mp) {
            path = path + `?marketplace=1`;
        }
        if (file_type) {
            path = path + `?file_type=${file_type}`;
        }
        return this.httpService.getFileAsBlob(path, 'jsonApi');
    }

    updateKitState(id, data) {
        const path = `products/${id}/change_kit_state`;
        return this.httpService.callPutApi(path, data, 'jsonApi');
    }

    uploadProductImages(id, files) {
        const path = 'products/' + id;
        return this.httpService.callUploadFilesPostServiceApi(
            path,
            files,
            'product_images',
            'jsonApi'
        );
    }

    updateProduct(data, id) {
        const path = `products/${id}/update_product`;
        return this.httpService.callPutApi(path, data, 'jsonApi');
    }

    getProductData(id) {
        const path = `products/${id}/product_details`;
        return this.httpService.callAuthGetApi(path, 'jsonApi').pipe(
            map((response: any) => {
                const hsCodes = response?.data?.attributes?.hs_code;
                if (!Array.isArray(hsCodes)) {
                    return response;
                }

                response.data.attributes.hs_code = [...hsCodes].sort(
                    (firstCode, secondCode) => {
                        const firstIsGlobal =
                            firstCode?.country_id == null ? 1 : 0;
                        const secondIsGlobal =
                            secondCode?.country_id == null ? 1 : 0;
                        return secondIsGlobal - firstIsGlobal;
                    }
                );

                return response;
            })
        );
    }

    getStockMovement(
        product_id,
        page,
        pageSize,
        warehouseId?,
        start_day?,
        end_day?
    ) {
        let path = `products/${product_id}/get_wms_stock_histories?warehouse_id=${warehouseId}&page[number]=${page}&page[size]=${pageSize}`;
        if (start_day && end_day) {
            path = `${path}&filter[start_date]=${start_day}&filter[end_date]=${end_day}`;
        }
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getCountryList() {
        const path = 'v1/masters/countries';
        return this.httpService.callAuthGetMasterApi(path);
    }
    public getBulkActivityProgressBar(id) {
        const path = `products/${id}/progress`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    public getInventoryCount() {
        const path = `products/counts`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }
    public getStorageTypes() {
        const path = 'products/storage_types';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }
    public getProductTypes() {
        const path = 'products/product_category';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }
    public getInventoryLocations(id) {
        const path = 'products/' + id + '/stocklocations';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }
    public getInventoryStock(id) {
        const path = 'products/' + id + '/inventory_stock';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }
    public gethistoryData(id) {
        const path = 'products/' + id + '/product_history';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }
    public getBatchData(id) {
        const path = 'products/' + id + '/batch_details';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    public getProductHistory(id) {
        const path = 'products/' + id + '/product_history';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    public syncMpProducts() {
        const path = 'marketplaces/sync_inventory_products';
        return this.httpService.callPutApi(path, {}, 'jsonApi');
    }
    public StocksyncProducts(filterarray?) {
        let path;
        if (filterarray) {
            path = `marketplaces/sync_inventory_stocks?${filterarray}`;
        } else {
            path = `marketplaces/sync_inventory_stocks`;
        }
        return this.httpService.callPutApi(path, {}, 'jsonApi');
    }

    public updateStateBulk(data) {
        const path = `products/bulk_update_state`;
        return this.httpService.callPutApi(path, data, 'jsonApi');
    }

    public checkKitMapping(productIds: string) {
        const path = 'products/check_kit_mapping';
        return this.httpService.callAuthPostApi(
            path,
            { product_ids: productIds },
            'jsonApi'
        );
    }

    public createKit(data) {
        const path = 'products/create_kit';
        return this.httpService.callAuthPostApi(path, data, 'jsonApi');
    }

    public createCategory(data) {
        const path = 'products/create_category';
        return this.httpService.callAuthPostApi(path, data, 'jsonApi');
    }

    public updateCreateProduct(data, id) {
        const path = `products/${id}/update_category`;
        return this.httpService.callPutApi(path, data, 'jsonApi');
    }

    public getCreateProductDetails(id) {
        const path = `products/${id}/category_details`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    public updateKitProduct(data, id) {
        const path = `products/${id}/kit_update`;
        return this.httpService.callPutApi(path, data, 'jsonApi');
    }

    public getKitProductDetails(id) {
        const path = `products/${id}/kit_details`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    public getMarketplacesPdtCount() {
        const path = 'products/marketplace_wise_count';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    public deleteCategory(id) {
        const path = `products/${id}/delete_category`;
        return this.httpService.callPutApi(path, {}, 'jsonApi');
    }

    public createBrand(data) {
        const path = 'products/brands';
        return this.httpService.callAuthPostApi(path, data, 'jsonApi');
    }

    public updateBrand(id, data) {
        const path = `products/brands/${id}`;
        return this.httpService.callPutApi(path, data, 'jsonApi');
    }

    public deleteBrand(id) {
        const path = 'products/brands';
        return this.httpService.callAuthDeleteApi<any>(
            path,
            { brand_ids: id },
            'jsonApi'
        );
    }

    public deleteBrands(ids: string[]) {
        const path = 'products/brands';
        return this.httpService.callAuthDeleteApi<any>(
            path,
            { brand_ids: ids.join(',') },
            'jsonApi'
        );
    }

    public getBrandDetails(id) {
        const path = `products/brands/${id}`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    public getBrandTemplateFile() {
        // Brand bulk upload template ID is 22 (WMS brand management)
        const path = 'v1/masters/templates?type=22';
        return this.httpService.getFileAsBlob(path);
    }

    public importBrands(file) {
        const path = 'products/brands/import';
        return this.httpService.callUploadFileServiceApi(path, file, 'jsonApi');
    }

    public exportBrands() {
        const path = 'products/brands/export';
        return this.httpService.getFileAsBlob(path, 'jsonApi');
    }

    public bulkUploadKitProductData(file) {
        const path = 'products/import_kit';
        return this.httpService.callUploadFileServiceApi(path, file, 'jsonApi');
    }

    public bulkUploadKitProduct(file) {
        const path = 'products/import_kit_product_oms';
        return this.httpService.callUploadFileServiceApi(path, file, 'jsonApi');
    }

    getProductKitTemplate() {
        const path = 'products/get_product_kit_template';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getOMSProductsCounts() {
        const path = 'marketplaces/connected/get_seller_marketplaces';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }
    resyncProducts(params) {
        const path = 'products/resync';
        return this.httpService.callAuthPostApi(path, params, 'jsonApi');
    }

    getUnsyncProductsCount() {
        const path = 'products/unsync_counts_v1';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getInboundOutboundDetails(product_id, type) {
        const path = `products/${product_id}/inbound_outbound_details?type=${type}`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getFormSettings(module_id, config_type) {
        const path = `products/${module_id}/module_sync_configuration?config_type=${config_type}`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }
    getBatches(product_id, page?) {
        const path = `orders/options?option_type=number&product_id=${product_id}`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }
    createUOM(data) {
        const path = 'products/create_uoms';
        return this.httpService.callAuthPostApi(path, data, 'jsonApi');
    }
    getISKUTemplateFileKit() {
        // For Bulk ISKU template ID is 20 and not 19
        const path = 'v1/masters/templates?type=20';
        return this.httpService.getFileAsBlob(path);
    }
    public bulkUploadISKU(file) {
        const path = 'products/import_sku_merge';
        return this.httpService.callUploadFileServiceApi(path, file, 'jsonApi');
    }
    getTemplateFileThreshold() {
        const path = 'v1/masters/templates?type=17';
        return this.httpService.getFileAsBlob(path);
    }
    public bulkUploadthreshold(file) {
        const path = 'products/import_threshold';
        return this.httpService.callUploadFileServiceApi(path, file, 'jsonApi');
    }
    getTemplateOffline() {
        const path = 'v1/masters/templates?type=19';
        return this.httpService.getFileAsBlob(path);
    }
    public bulkUploadOfflineKitProduct(file) {
        const path = 'products/import_offline_kit_product';
        return this.httpService.callUploadFileServiceApi(path, file, 'jsonApi');
    }
    getGridData(path) {
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getAllListingStates() {
        const path = 'products/all_listing_state';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }
    public getsyncStockDetails() {
        const path = 'marketplaces/sync_details';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }
    public postForceSyncStock(data: any) {
        const path = 'marketplaces/sync_stock';
        return this.httpService.callAuthPostApi(path, data, 'jsonApi');
    }
    getproductunmappedTemplateFile() {
        const path = 'marketplaces/get_bulk_import_versions';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }
    getMpList() {
        const path = 'marketplaces/connected/get_seller_marketplaces';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }
    bulkUploadProductunmapped(formdata, file) {
        const path = 'marketplaces/unmapping_product_by_sku';

        return this.httpService.callUploadFileServiceApi(
            path,
            file,
            'jsonApi',
            0,
            formdata
        );
    }
}
@Injectable({ providedIn: 'root' })
export class productStock extends Resource {
    public attributes = {
        type: '',
        id: ''
    };
}
@Injectable({
    providedIn: 'root'
})
export class productStockService extends AxiosAuthenticationService<productStock> {
    public resource = productStock;
    public type = 'product_stock';
}

@Injectable({ providedIn: 'root' })
export class ProductHistory extends Resource {
    public attributes = {
        type: ''
    };
    public relationships = {
        user: new DocumentResource<User>()
    };
}
@Injectable({
    providedIn: 'root'
})
export class ProductHistoryService extends AxiosAuthenticationService<ProductHistory> {
    public resource = ProductHistory;
    public type = 'product_history';
}

@Injectable({ providedIn: 'root' })
export class User extends Resource {
    public attributes = {
        type: '',
        id: ''
    };
}
@Injectable({
    providedIn: 'root'
})
export class UserService extends AxiosAuthenticationService<User> {
    public resource = User;
    public type = 'user';
}

export class KitProduct extends Resource {
    public attributes = {
        type: '',
        id: ''
    };
    public relationships = {
        kit_mappings: new DocumentCollection<KitMapping>()
        // storage_type: new DocumentResource<storageType>(),
        // product_stock: new DocumentResource<productStock>()
    };
}

@Injectable({
    providedIn: 'root'
})
export class KitProductsService extends AxiosAuthenticationService<KitProduct> {
    public resource = KitProduct;
    public type = 'kit_products';
}

@Injectable({ providedIn: 'root' })
export class KitMapping extends Resource {
    public attributes = {
        type: '',
        id: ''
    };
}
@Injectable({
    providedIn: 'root'
})
export class kitmappingsService extends AxiosAuthenticationService<KitMapping> {
    public resource = KitMapping;
    public type = 'kit_mappings';
}

export class UnsyncedProduct extends Resource {
    public attributes = {
        type: '',
        id: ''
    };
    public relationships = {};
}

@Injectable({
    providedIn: 'root'
})
export class AllUnsyncedProductService extends AxiosAuthenticationService<UnsyncedProduct> {
    public resource = UnsyncedProduct;
    public type = 'product_issues';
}

export class UnsyncedOfflineProducts extends Resource {
    public attributes = {
        type: '',
        id: ''
    };
    public relationships = {};
}

@Injectable({
    providedIn: 'root'
})
export class AllUnsyncedOfflineProductsService extends AxiosAuthenticationService<UnsyncedOfflineProducts> {
    public resource = UnsyncedOfflineProducts;
    public type = 'unsync_offline_product';
}
